// Auth endpoints answer with an error code plus an English message. Showing
// that message verbatim leaks English into the Arabic UI, so known codes get
// translated copy and anything unknown falls back to the server text.
// (issue #253: the OTP lockout responses were the visible case.)

export const AUTH_ERROR_COPY_KEYS: Record<string, string> = {
  OTP_LOCKED: 'login.otpLocked',
  OTP_RESEND_COOLDOWN: 'login.otpResendCooldown',
  OTP_TOO_MANY_ATTEMPTS: 'login.otpTooManyAttempts',
  EMAIL_SEND_FAILED: 'login.otpSendError',
  // Better Auth's native id-token sign-in (Apple on iOS) rejects an unlinkable
  // account as JSON rather than the redirect the browser flow uses, so the raw
  // English "account not linked" would otherwise reach the toast verbatim.
  OAUTH_LINK_ERROR: 'login.accountNotLinked',
};

export function getAuthErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

export function getAuthErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() ? message : null;
}

/**
 * Resolves the user-facing copy for an auth failure: translated copy when the
 * server code is one we know, the server message when it is not, and the
 * caller's fallback when there is no message at all.
 */
export function getAuthErrorCopy(
  error: unknown,
  t: (key: string) => string,
  fallback: string,
): string {
  const code = getAuthErrorCode(error);
  const key = code ? AUTH_ERROR_COPY_KEYS[code] : undefined;
  if (key) {
    const translated = t(key);
    // i18next returns the key itself when the string is missing.
    if (translated && translated !== key) return translated;
  }

  return getAuthErrorMessage(error) ?? fallback;
}
